import { Breadcrumbs, Grid, Link, Stack, Typography } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import {
  Link as RouterLink,
  Navigate,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom'

import PageContainer from 'components/common/PageContainer'
import PageSection from 'components/common/PageSection'
import Icon from 'components/Icon'
import { Header } from 'components/navigation/Header'
import { DeleteStatusCheckAuthentication } from 'components/status-checks/common/DeleteStatusCheckAuthentication'
import {
  findMatchingIntegrationByAttributes,
  getDisplayIntegrationName
} from 'components/status-checks/edit/edit-authentication-utils'
import { EditAuthenticationForm } from 'components/status-checks/edit/EditAuthenticationForm'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import {
  INITIAL_AUTHENTICATION_RESPONSE,
  useObservabilityToolAuthenticationContext
} from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import { muiTheme } from 'mui-theme'

export const EditAuthentication: React.FC = () => {
  const statusChecksUrls = getStatusChecksUrls()
  const navigate = useNavigate()
  const location = useLocation()
  const { state: locationState } = location
  const { referrer = statusChecksUrls.base() } = locationState ?? {}

  // These come from the route where 'domain' is an optional segment
  const { domain, observabilityToolType, integrationName } = useParams()

  const {
    setAuthenticationResponse,
    setIsCreatingAuthenticationOnly,
    setIsEdit
  } = useObservabilityToolAuthenticationContext()

  const {
    hasErrorExternalIntegrations,
    isFetchingExternalIntegrations,
    getAvailableExternalIntegrations
  } = useStatusCheckExternalIntegrationsContext()

  const externalIntegrations = getAvailableExternalIntegrations(false)
  const selectedExternalIntegration = useMemo(
    () =>
      integrationName && observabilityToolType
        ? externalIntegrations.find(
            findMatchingIntegrationByAttributes(
              domain,
              integrationName,
              observabilityToolType
            )
          )
        : undefined,
    [integrationName, domain, observabilityToolType, externalIntegrations]
  )

  // These useEffects seem to be a necessary pattern commonly found in consumers of 'ObservabilityToolAuthenticationContext'
  useEffect(() => {
    setIsCreatingAuthenticationOnly(true)
    setIsEdit(true)
  }, [setIsCreatingAuthenticationOnly, setIsEdit])

  /*
   * If we leave the form, make sure we reset the authentication response state so it does not persist
   * across multiple form edits (EN-9190).
   */
  useEffect(() => {
    return () => {
      setAuthenticationResponse(INITIAL_AUTHENTICATION_RESPONSE)
    }
  }, [setAuthenticationResponse])

  /*
   * We already presume to re-route users in this manner via the `StatusChecksIndex` component.
   * This adds an extra layer of type safety to satisfy our linter.
   */
  if (!integrationName || !observabilityToolType) {
    return <Navigate to={statusChecksUrls.authentication()} />
  }

  const displayIntegrationName = getDisplayIntegrationName({
    domain,
    integrationName,
    observabilityToolType
  })

  return (
    <>
      <Helmet title="Edit Authentication" />
      <Header>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            data-test-status-check-back-link
            component={RouterLink}
            to={referrer === '/' ? statusChecksUrls.base() : referrer}
            underline="hover"
          >
            <Icon css={{ marginRight: muiTheme.spacing(1) }} icon="wavePulse" />
            Health Checks
          </Link>
          <Link
            data-test-status-check-auth-list-link
            component={RouterLink}
            to={statusChecksUrls.authentication()}
            underline="hover"
          >
            Authentication List
          </Link>
          <Typography color="text.primary">Edit Authentication</Typography>
        </Breadcrumbs>
      </Header>
      <PageContainer>
        <Stack mb={3} spacing={3}>
          <Grid container gap={2}>
            <Grid item>
              <Typography data-test-edit-status-check-title variant="h5">
                {`Edit ${displayIntegrationName}`}
              </Typography>
            </Grid>
            <Grid item>
              <DeleteStatusCheckAuthentication
                buttonProps={{ size: 'small' }}
                integration={selectedExternalIntegration}
                onDeleteComplete={() => {
                  navigate(statusChecksUrls.authentication())
                }}
              />
            </Grid>
          </Grid>
        </Stack>
        <PageSection
          errorMessage={
            hasErrorExternalIntegrations
              ? 'There was an issue loading the available authentications. Please try again later.'
              : 'Unable to find the selected authentication. Check that the URL is correct.'
          }
          hasError={
            hasErrorExternalIntegrations ||
            (!isFetchingExternalIntegrations && !selectedExternalIntegration)
          }
          isLoading={isFetchingExternalIntegrations}
        >
          {externalIntegrations && selectedExternalIntegration && (
            <EditAuthenticationForm
              externalIntegration={selectedExternalIntegration}
              externalIntegrations={externalIntegrations}
            />
          )}
        </PageSection>
      </PageContainer>
    </>
  )
}
